import { defineStore } from 'pinia'
import { shallowRef } from 'vue'

export type EntryUploadBufferedFile = {
  id: string
  file: File
  fileName: string
  previewUrl: string
  width?: number
  height?: number
  assetId?: string
  stockAssetId?: string
}

export type EntryUploadApplyPayload = {
  productId: number
  productTitle?: string
  categoryId: number
  categoryTitle?: string
  categoryThumbnail?: string
  sizeVariantId?: string
  sizeTitle?: string
  sizeWidthMm?: number
  sizeHeightMm?: number
  options: Array<{ groupId: string; variantId: string; value: string }>
}

export type EntryUploadHandoff = {
  files: EntryUploadBufferedFile[]
  payload: EntryUploadApplyPayload
}

export const useEntryUploadHandoffStore = defineStore('entryUploadHandoffStore', () => {
  const current = shallowRef<EntryUploadHandoff | null>(null)

  // Revoke the blob: object URLs held by a buffered handoff that is being
  // dropped/replaced. Guards against non-blob URLs (e.g. http remote URLs that
  // we do not own) and tolerates double-revoke via try/catch.
  function revokeHandoffUrls(handoff: EntryUploadHandoff | null) {
    if (!handoff?.files?.length) return
    for (const file of handoff.files) {
      const url = file?.previewUrl
      if (typeof url === 'string' && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url) } catch {}
      }
    }
  }

  function set(handoff: EntryUploadHandoff | null) {
    // Replacing (or clearing via set(null)) the buffered handoff: revoke the old
    // value's URLs before discarding it, unless it is the same object reference.
    if (current.value && current.value !== handoff) revokeHandoffUrls(current.value)
    current.value = handoff
  }

  // NOTE: consume() hands ownership of the buffered files (and their blob: URLs)
  // back to the caller, which still needs previewUrl alive (e.g. multi.vue passes
  // them straight into importEntryBufferedUploads). We therefore do NOT revoke
  // here — the caller owns the URLs once consumed. Revocation happens on
  // clear()/set() replacement instead.
  function consume(): EntryUploadHandoff | null {
    const next = current.value
    current.value = null
    return next
  }

  function peek(): EntryUploadHandoff | null {
    return current.value
  }

  function clear() {
    // Dropping the buffered handoff without returning it to a caller: revoke now.
    revokeHandoffUrls(current.value)
    current.value = null
  }

  return {
    current,
    set,
    consume,
    peek,
    clear,
  }
})
